import { Button } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { v4 as uuid } from 'uuid';

import { Text } from 'bones/components/atoms/Text';
import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { SubExpressionBlock } from 'bones/components/molecules/QueryBuilder/SubExpressionBlock';
import { createNewSubExpression } from 'bones/utils/queryBuilder/helpers/create';
import {
  DraftSubExpression,
  DraftTopExpression,
} from 'bones/utils/queryBuilder/types';

export interface TopExpressionBlockProps {
  setTopExpression: (
    topExpression: DraftTopExpression,
    validate?: boolean
  ) => void;
}

export const TopExpressionBlock: React.VFC<TopExpressionBlockProps> = ({
  setTopExpression,
}) => {
  const { copy, testIds } = useQueryBuilderSchemaContext();
  const { topExpression } = useQueryBuilderContext();
  const { orDisabled } = useQueryBuilderSchemaContext();
  const { expressions } = topExpression;

  const addSubExpression = () => {
    expressions.push(createNewSubExpression());
    setTopExpression({ ...topExpression, expressions });
  };

  const updateSubExpression =
    (idx: number) =>
    (subExpression: DraftSubExpression, validate?: boolean) => {
      const expressions = [...topExpression.expressions];
      if (subExpression.expressions.length) {
        expressions.splice(idx, 1, subExpression);
      } else {
        expressions.splice(idx, 1);
      }

      setTopExpression(
        {
          ...topExpression,
          expressions: expressions.length
            ? expressions
            : // istanbul ignore next
              [createNewSubExpression()],
        },
        validate
      );
    };

  const duplicateSubExpression = (idx: number) => () => {
    const subExpressions = [...topExpression.expressions];
    const { conjunction, expressions } = subExpressions[idx];
    const duplicateIds: Record<string, string> = {};
    const getDuplicateId = (id: string) => {
      if (!duplicateIds[id]) {
        duplicateIds[id] = uuid();
      }
      return duplicateIds[id];
    };
    subExpressions.splice(idx, 0, {
      id: uuid(),
      expressions: expressions.map(({ filters }) => ({
        id: uuid(),
        filters: filters.map((filter) => ({
          ...filter,
          id: uuid(),
          subject: filter.subject
            ? {
                ...filter.subject,
                id: getDuplicateId(filter.subject.id),
              }
            : null,
          value:
            filter.value && typeof filter.value !== 'string'
              ? {
                  ...filter.value,
                  id: getDuplicateId(filter.value.id),
                }
              : filter.value,
        })),
      })),
      conjunction,
    });

    setTopExpression({ ...topExpression, expressions: subExpressions }, true);
  };

  return (
    <div
      className="sd-query-builder-top-expression"
      data-testid={testIds.topExpressionBlock}
    >
      {expressions.map((subExpression, idx) => {
        const isLastElement = idx === expressions.length - 1;
        let conjunctionEl: JSX.Element | null = null;
        if (!isLastElement) {
          conjunctionEl = (
            <Text className="sd-query-builder-top-expression-conjunction">
              {copy.body.conjunction.or}
            </Text>
          );
        }

        return (
          <React.Fragment key={subExpression.id}>
            <SubExpressionBlock
              duplicateSubExpression={duplicateSubExpression(idx)}
              subExpressionIdx={idx}
              updateSubExpression={updateSubExpression(idx)}
            />
            {conjunctionEl}
          </React.Fragment>
        );
      })}
      {orDisabled ? null : (
        <Button
          data-testid={testIds.addSubExpressionButton}
          icon={IconNames.ADD}
          onClick={addSubExpression}
          text={copy.body.conjunction.or}
        />
      )}
    </div>
  );
};
